// Overlay panel shown when the ActivityFloatingDock is open. Floats over
// page content (no reflow), anchored to whichever edge the dock tab is on.
//
// Phase 4 (docs/planning/org_activity_floating_dock.md §12): the panel is
// edge-aware:
//   - left/right: anchored to that side, fixed width, slides in from the
//     edge.
//   - top/bottom: full-width drawer flush against the top/bottom edge,
//     slides down (top) / up (bottom).
//
// Slice P (docs/planning/activity_dock_workflow_facilitation.md §6): the
// panel no longer composes individual sections inline. Instead it iterates
// `useActivityDockSections()` and renders each registered entry in order.
// Adding/reordering/role-gating sections is a registry-only change going
// forward; this file is just shell chrome (edge math, focus/escape
// handling, header).

import { useEffect, useRef, useState, type RefObject } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { getBucketForMode } from "@/components/shell/modes/bucketsRegistry";
import { useActivityDockSections } from "./useActivityDockSections";
import { useActivityDockPanelDismiss } from "./useActivityDockPanelDismiss";
import { isHorizontalEdge, type DockEdge } from "./useActivityDockPosition";
import { activityDockExpandedPanelStyle, activityDockExpandedPanelDragStyle } from "./activityDockPanelStyle";
import { formatImpersonationBannerCopy } from "./impersonationBanner";
import { ActivityDockFireflyStatusBanner } from "./ActivityDockFireflyStatusBanner";
import type { KpiTone } from "@/components/common/kpi/kpiTypes";
import type { FireflyAskHighlightRequest } from "@/components/activity/ActivityDockAskFirefly";
import type { ActivityDockAskFireflyHandle } from "@/components/activity/firefly/useActivityDockAskFireflyController";
import type { FireflyTourArtifact, FireflyTourTrigger } from "@/lib/firefly/tourTypes";
import type { PendingMemberApprovalCounts } from "@/lib/admin/pendingMemberApprovalCount";
import { ActivityDockPanelHeader } from "./ActivityDockPanelHeader";
import { ActivityAttentionGroups } from "@/components/attention/ActivityAttentionGroups";
import { useAttentionList } from "@/components/attention/useAttentionList";
import { useBellHostPanelStyle } from "@/components/attention/useBellHostPanelStyle";
import { ActivityDockPrimaryTabs } from "@/components/activity/ActivityDockPrimaryTabs";
import type { ActivityDockPointerHandlers } from "./useActivityDockTapVsDrag";

export type ActivityDockExpandedPanelProps = {
  edge: DockEdge;
  offsetRatio: number;
  /** Width of the tab along the screen edge it's anchored to. */
  tabWidthPx: number;
  /** Height of the tab perpendicular to the screen edge. */
  tabHeightPx: number;
  /** While repositioning, follow the drag preview instead of the edge anchor. */
  dragPreview?: { x: number; y: number } | null;
  /** Drag the header chrome to move Firefly + panel together. */
  panelDragHandlers?: ActivityDockPointerHandlers;
  /**
   * Slice B2.1 — optional outer ref the dock parent can attach to the
   * panel root. The peek lifecycle hook (`useActivityDockModePeek`)
   * uses it to attach in-panel interaction listeners that cancel an
   * in-flight auto-close timer. Falls through to a local ref when
   * omitted so the panel still works in isolation (tests / Storybook).
   */
  outerRef?: RefObject<HTMLDivElement | null>;
  onClose: () => void;
  onOpenAdsIngestion: () => void;
  onOpenAdsLibrary: () => void;
  /**
   * Optional sections-context wiring used by the Slice C daily
   * checklist section. Omitting these keeps the section functional —
   * the widget already degrades the row CTAs and follow-up alert to
   * no-ops when their callbacks are missing.
   */
  onOpenAdsAssistant?: () => void;
  onOpenAdsAssistantSuggestion?: () => void;
  onOpenSalesConsole?: () => void;
  /** WF-0 — platform-context section navigates to Platform home. */
  onOpenPlatform?: () => void;
  /** SC-5 — manager-bucket signals threaded into section render context. */
  kbGapOpenCount?: number | null;
  onOpenKbGapInbox?: () => void;
  aiPromptingOpenCount?: number | null;
  onOpenAiPrompting?: () => void;
  pendingMemberApprovalCounts?: PendingMemberApprovalCounts | null;
  pendingMemberApprovalTotal?: number | null;
  onOpenAdmin?: () => void;
  orgUnsentRollupCount?: number | null;
  onOpenDashboard?: () => void;
  staleWacCount?: number | null;
  onOpenMfgInventory?: () => void;
  plantRequestReviewCount?: number | null;
  plantRequestStatusItems?: ReadonlyArray<{ id: string; poNumber: string; copy: string }> | null;
  onOpenMfgPurchasing?: () => void;
  openQcItems?: ReadonlyArray<{
    id: string;
    description: string;
    serial_text: string | null;
    session_id: string;
    category_id?: string | null;
    category_name?: string | null;
    item_title?: string | null;
  }> | null;
  onOpenMfgBuilderAssistant?: (sessionId?: string, categoryId?: string | null) => void;
  followUpAttentionCount?: number;
  /** Slice D — passed through to the unsent-leads section's "Send all". */
  onSendAllUnsent?: () => void | Promise<void>;
  isSending?: boolean;
  /**
   * Slice B2.5 — current workspace primary mode, forwarded to each
   * section's render context so sections can shape their copy /
   * sub-labels (NOT their render visibility) to where the user is.
   * Optional + defaults to `null` so existing callers and tests that
   * don't thread mode keep working in the brand-neutral copy path.
   */
  currentMode?: PrimaryMode | null;
  /**
   * RP-5 — persistent impersonation banner. When `impersonating` is
   * true, the panel renders a purple non-dismissable banner row
   * between the header and the section list listing the impersonated
   * role(s). Both default to "no impersonation" so pre-RP-5 callers
   * (and tests that mount the panel standalone) keep working.
   */
  impersonating?: boolean;
  impersonatingRoles?: readonly string[];
  /** Critical jitter calm — keeps red intent, disables position jitter. */
  showStopBlinking?: boolean;
  onStopBlinking?: () => void;
  /**
   * Why Firefly is not in ordinary (Focus) state. When both are set,
   * a status banner renders under the header so the glow color is
   * explained in plain language.
   */
  fireflyStatusTone?: KpiTone | null;
  fireflyStatusReasonLabel?: string | null;
  /** ASK-1 — navigate to a primary mode from Ask Firefly answers. */
  onNavigateToMode?: (mode: PrimaryMode) => void;
  scopedOrganizationId?: string | null;
  onHighlightFireflyTarget?: (request: FireflyAskHighlightRequest) => void;
  /** App tour — start multi-step session from Ask match. */
  onStartFireflyTour?: (artifact: FireflyTourArtifact, trigger?: FireflyTourTrigger) => void;
  /** PTT-4 — parent-owned Ask ref for tab long-press voice entry. */
  askRef?: RefObject<ActivityDockAskFireflyHandle | null>;
  tabPttListening?: boolean;
  /** Docked housing: panel anchors under the notification bell. */
  bellHost?: boolean;
  negativeOnHandCount?: number | null;
};

function useActivityDockPanelViewport() {
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1280,
    h: typeof window !== "undefined" ? window.innerHeight : 800,
  }));

  useEffect(() => {
    const onResize = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return viewport;
}

/** Slide-in animation class for the snapped edge. */
function slideInClassForEdge(edge: DockEdge): string {
  switch (edge) {
    case "left":
      return "activity-dock-slide-from-left";
    case "right":
      return "activity-dock-slide-from-right";
    case "top":
      return "activity-dock-slide-from-top";
    case "bottom":
      return "activity-dock-slide-from-bottom";
  }
}

export function ActivityDockExpandedPanel({
  edge,
  offsetRatio,
  tabWidthPx,
  tabHeightPx,
  dragPreview = null,
  panelDragHandlers,
  outerRef,
  onClose,
  onOpenAdsIngestion,
  onOpenAdsLibrary,
  onOpenAdsAssistant,
  onOpenAdsAssistantSuggestion,
  onOpenSalesConsole,
  followUpAttentionCount,
  onSendAllUnsent,
  isSending,
  currentMode = null,
  onOpenPlatform,
  kbGapOpenCount,
  onOpenKbGapInbox,
  aiPromptingOpenCount,
  onOpenAiPrompting,
  pendingMemberApprovalCounts,
  pendingMemberApprovalTotal,
  onOpenAdmin,
  orgUnsentRollupCount,
  onOpenDashboard,
  staleWacCount,
  onOpenMfgInventory,
  plantRequestReviewCount,
  plantRequestStatusItems,
  onOpenMfgPurchasing,
  openQcItems,
  onOpenMfgBuilderAssistant,
  impersonating = false,
  impersonatingRoles = [],
  showStopBlinking = false,
  onStopBlinking,
  fireflyStatusTone = null,
  fireflyStatusReasonLabel = null,
  onNavigateToMode,
  scopedOrganizationId = null,
  onHighlightFireflyTarget,
  onStartFireflyTour,
  askRef,
  tabPttListening = false,
  bellHost = false,
  negativeOnHandCount = 0,
}: ActivityDockExpandedPanelProps) {
  const localRef = useRef<HTMLDivElement>(null);
  const ref = outerRef ?? localRef;
  // Derive `currentBucket` once and use it for both the section-list
  // filter (`useActivityDockSections`, L2) and the render context
  // forwarded to each section's render fn (RP-7). One source of
  // truth for "which bucket the dock is operating in this frame."
  // Null when `currentMode` is null (test mounts / early bootstrap);
  // both consumers fail-open on null.
  const currentBucket = currentMode ? getBucketForMode(currentMode) : null;
  const sections = useActivityDockSections(currentBucket);
  const attention = useAttentionList({
    currentMode,
    openQcCount: openQcItems?.length ?? 0,
    negativeOnHandCount: negativeOnHandCount ?? 0,
    plantRequestReviewCount: plantRequestReviewCount ?? 0,
    plantStatusCount: plantRequestStatusItems?.length ?? 0,
    staleWacCount: staleWacCount ?? 0,
    kbGapOpenCount: kbGapOpenCount ?? 0,
    aiPromptingOpenCount: aiPromptingOpenCount ?? 0,
    pendingMemberApprovalTotal: pendingMemberApprovalTotal ?? 0,
    orgUnsentRollupCount: orgUnsentRollupCount ?? 0,
    onOpenLeads: true,
    onOpenBuilderAssistant: Boolean(onOpenMfgBuilderAssistant),
    onOpenInventory: Boolean(onOpenMfgInventory),
    onOpenPurchasing: Boolean(onOpenMfgPurchasing),
    onOpenAds: Boolean(onOpenAdsIngestion),
    onOpenKb: Boolean(onOpenKbGapInbox),
    onOpenAiPrompting: Boolean(onOpenAiPrompting),
    onOpenAdmin: Boolean(onOpenAdmin),
  });
  useActivityDockPanelDismiss(ref, onClose);

  const viewport = useActivityDockPanelViewport();
  const bellStyle = useBellHostPanelStyle(bellHost);
  const style = bellStyle
    ? bellStyle
    : dragPreview
    ? activityDockExpandedPanelDragStyle({
        x: dragPreview.x,
        y: dragPreview.y,
        viewportW: viewport.w,
        viewportH: viewport.h,
      })
    : activityDockExpandedPanelStyle({
        edge,
        offsetRatio,
        tabWidthPx,
        tabHeightPx,
        viewportW: viewport.w,
        viewportH: viewport.h,
      });
  const roundingCls = bellHost || dragPreview
    ? "rounded-xl"
    : isHorizontalEdge(edge)
      ? edge === "top"
        ? "rounded-b-xl"
        : "rounded-t-xl"
      : "rounded-xl";

  const renderCtx = {
    currentMode,
    currentBucket,
    onOpenAdsIngestion,
    onOpenAdsLibrary,
    onCloseDock: onClose,
    onOpenAdsAssistant,
    onOpenAdsAssistantSuggestion,
    onOpenSalesConsole,
    followUpAttentionCount,
    onSendAllUnsent,
    isSending,
    onOpenPlatform,
    kbGapOpenCount,
    onOpenKbGapInbox,
    aiPromptingOpenCount,
    onOpenAiPrompting,
    pendingMemberApprovalCounts,
    pendingMemberApprovalTotal,
    onOpenAdmin,
    orgUnsentRollupCount,
    onOpenDashboard,
    staleWacCount,
    onOpenMfgInventory,
    plantRequestReviewCount,
    plantRequestStatusItems,
    onOpenMfgPurchasing,
    openQcItems,
    onOpenMfgBuilderAssistant,
  };

  // RP-5 — persistent impersonation banner. Lives in chrome (not in a
  // dock section) so it can't be hidden by section-list filtering or
  // scrolled off-screen. No close button by design — closes only when
  // impersonation itself ends (the user exits the impersonation
  // session via DevPanel). See `firefly_role_context_presentation.md`
  // §RP-5 + §5.4 last row for the doctrine.
  const impersonationBanner = formatImpersonationBannerCopy(
    impersonating,
    impersonatingRoles,
  );

  return (
    <div
      ref={ref}
      data-app-panel="ActivityDockExpandedPanel"
      role="dialog"
      aria-modal={false}
      aria-label="Activity"
      style={style}
      className={[
        "z-[9150] overflow-y-auto border border-gray-200 bg-white p-3 shadow-2xl",
        "dark:border-gray-700 dark:bg-zinc-900",
        roundingCls,
        slideInClassForEdge(edge),
      ].join(" ")}
    >
      <ActivityDockPanelHeader
        panelDragHandlers={panelDragHandlers}
        showStopBlinking={showStopBlinking}
        onStopBlinking={onStopBlinking}
        onClose={onClose}
      />

      <ActivityDockFireflyStatusBanner
        tone={fireflyStatusTone}
        reasonLabel={fireflyStatusReasonLabel}
      />

      {impersonationBanner ? (
        <div
          role="status"
          aria-live="polite"
          className="mb-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] font-medium text-amber-900 dark:border-amber-500/60 dark:bg-amber-950/40 dark:text-amber-200"
        >
          {impersonationBanner}
        </div>
      ) : null}

      <ActivityDockPrimaryTabs
        askRef={askRef}
        askProps={{
          currentMode: currentMode ?? null,
          scopedOrganizationId,
          onNavigateToMode,
          onCloseDock: onClose,
          onHighlightTarget: onHighlightFireflyTarget,
          onStartTour: onStartFireflyTour,
          tabPttListening,
        }}
        currentMode={currentMode ?? null}
        onCloseDock={onClose}
      />

      <ActivityAttentionGroups
        sections={sections}
        groups={attention.groups}
        renderCtx={renderCtx}
        showNegativeOnHand={attention.showNegativeOnHand}
        negativeOnHandCount={negativeOnHandCount ?? 0}
      />
    </div>
  );
}
